import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  getColorScheme,
  setColorScheme,
  ScaleButton,
  ScaleIconActionLightDarkMode,
  ScaleIconNavigationCollapseDown,
  ScaleLogo,
  type ColorScheme,
} from '@cloudorder/ui';

import { PixieLauncher } from '@/features/pixie/PixieLauncher';
import { useAuth } from '@/shared/auth/AuthContext';
import { environmentLabel } from '@/shared/config/env';
import { LocaleSwitcher } from '@/shared/i18n';

import { Omnibox } from './Omnibox';
import styles from './TopBar.module.css';
import { UserProfileMenu } from './UserProfileMenu';

export function TopBar(): ReactElement {
  const { user, logout } = useAuth();
  const { t } = useTranslation(['app', 'tenancy']);
  const [scheme, setScheme] = useState<ColorScheme>(() => getColorScheme());

  /* `||` and not `??`: an identity with no display name arrives as `""`, not as
     an absent field — the service reads the `name` claim out of the id_token and
     an id_token without one yields the empty string. `??` would keep that empty
     string, and since the menu is only rendered when there is a name at all, the
     result is a bar with no way to sign out rather than a blank label. */
  const displayName: string = user?.name || user?.email || '';
  const email: string = user?.email ?? '';

  function toggleScheme(): void {
    const next: ColorScheme = scheme === 'dark' ? 'light' : 'dark';
    setColorScheme(next);
    setScheme(next);
  }

  return (
    <header className={styles.topbar} role="banner">
      <ScaleLogo className={styles.logo} size={30} />
      <Link className={styles.appname} to="/">
        {/* The product name is a proper noun and stays as it is in every
            language; only the line under it describes what the product does. */}
        CloudOrder<span>{t('tagline')}</span>
      </Link>

      <Omnibox />

      <div className={styles.spacer} />

      <div className={styles.utilities}>
        <div className={styles.actionGroup}>
          <PixieLauncher />

          <span className={styles.localeToggle}>
            <LocaleSwitcher />
          </span>

          <span className={styles.themeToggle}>
            <ScaleButton
              size="small"
              variant="secondary"
              iconOnly
              innerAriaLabel={scheme === 'dark' ? t('appearance.toLight') : t('appearance.toDark')}
              onClick={toggleScheme}
            >
              <ScaleIconActionLightDarkMode decorative />
            </ScaleButton>
          </span>
        </div>

        <span className={styles.separator} aria-hidden="true" />

        <div className={styles.contextGroup}>
          {/* Which tenant the rows on screen belong to, and the way to change it.
              Rendered only once a tenant is chosen — before that the operator is
              looking at the picker itself, and a chip pointing back at it would be
              a link to the page they are on.

              No `next` on the link, deliberately. A switch that returned to the
              screen just left would land a deep link belonging to the previous
              tenant — a case id that the new tenant does not have — so switching
              starts again at the overview. */}
          {user?.tenantName == null ? null : (
            <Link className={styles.tenant} to="/select-tenant" title={t('tenancy:select.switch')}>
              <span className={styles.tenantName}>{user.tenantName}</span>
              <ScaleIconNavigationCollapseDown size={14} decorative />
            </Link>
          )}

          <span
            className={styles.env}
            title={t('environment.label', { environment: environmentLabel })}
          >
            {environmentLabel}
          </span>
        </div>

        {displayName ? (
          <>
            <span className={styles.separator} aria-hidden="true" />
            <UserProfileMenu name={displayName} email={email} onLogout={logout} />
          </>
        ) : null}
      </div>
    </header>
  );
}
